import React, { useEffect, useRef } from 'react';
import {
  AuthType,
  init,
  AppEmbed as TSAppEmbed,
  Action,
  Page,
} from '@thoughtspot/visual-embed-sdk';
import BEM from 'common/bem';

const classes = BEM.with('ClinicalWorkflows');

type Props = {
  host: string;
  token: string;
  orgId: number;
};

export default function AppEmbedComponent({ host, token, orgId }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const initializedHostRef = useRef<string | null>(null);
  // Always holds the latest token so the getAuthToken closure never goes stale
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (!containerRef.current) return;

    if (initializedHostRef.current !== host) {
      init({
        thoughtSpotHost: host,
        authType: AuthType.TrustedAuthTokenCookieless,
        getAuthToken: () => Promise.resolve(tokenRef.current),
        customizations: {
          style: {
            customCSS: {
              // rules_UNSTABLE targets ThoughtSpot internal selectors.
              // Class names sourced from the live embed DOM — may need updates on TS upgrades.
              rules_UNSTABLE: {
                // Hide bulk-selection action bar buttons not covered by hiddenActions enum
                '[data-testid="add-to-collection-action"]': {
                  display: 'none !important',
                },
                // Hide the per-row "..." context menu button
                '[data-testid="action-menu-button"]': {
                  display: 'none !important',
                },
              },
            },
          },
        },
      });
      initializedHostRef.current = host;
    }

    const embed = new TSAppEmbed(containerRef.current, {
      pageId: Page.Liveboards,
      overrideOrgId: orgId,
      showPrimaryNavbar: false,
      disableProfileAndHelp: true,
      hiddenActions: [Action.Share, Action.ExportTML],
    });

    embed.render();

    return () => {
      embed.destroy();
    };
  }, [host, orgId]);

  return <div ref={containerRef} className={classes('liveboard-embed')} />;
}
